import {HexMath} from '../HexMath';
import {checkIfFlag} from '../buildings/checkIfFlag';
import {checkIfCentralQuarter} from '../buildings/checkIfCentralQuarter';
import {getGridGeometry} from '../gameState/progress/getGridGeometry';
import {getPlayerHexColor} from '../players/getPlayerHexColor';
import {DEFAULT_GRID_CONFIG} from '../../constants/gridConfig';
import {CENTRAL_QUARTER_PREFIX, FLAG_PREFIX, TERRAIN_TOKENS} from '../../constants/tokens';
import {
  FIELD_PREVIEW_CENTRAL_QUARTER_FILL,
  FIELD_PREVIEW_CENTRAL_QUARTER_LEGEND_ID,
  FIELD_PREVIEW_FLAG_CELL_OPACITY,
  FIELD_PREVIEW_FLAG_LEGEND_ID,
  FIELD_PREVIEW_MARKS,
  FIELD_PREVIEW_OTHER_TOKEN,
  FIELD_PREVIEW_TOKEN_STYLES,
  FIELD_PREVIEW_UNIT_LEGEND_ID,
} from '../../configs/fieldPreview';

// Зазор между жетоном и линиями сетки, в долях ячейки
const TOKEN_HEX_SCALE = 0.86;
const INNER_HEX_SCALE = 0.42;
// Запас вокруг поля, в долях ячейки: чтобы линии крайних ячеек не срезались
const VIEW_BOX_PADDING = 0.25;

// В точках поля (ячейка — десятки точек) дроби на схеме не видны, а целые
// заметно сокращают разметку: в ней сотни ячеек
const formatPath = (points) => `M${points.map(({x, y}) => `${Math.round(x)} ${Math.round(y)}`).join('L')}Z`;

// Метка в долях ячейки от ее центра
const shapeAt = (center, hexSize, shape) => formatPath(
  shape.map(([x, y]) => ({x: center.x + x * hexSize, y: center.y + y * hexSize}))
);

const PEAK_SHAPE = [[0, -0.56], [0.52, 0.4], [-0.52, 0.4]];
const FLAG_POLE_SHAPE = [[-0.3, -0.6], [-0.2, -0.6], [-0.2, 0.6], [-0.3, 0.6]];
const FLAG_CLOTH_SHAPE = [[-0.2, -0.6], [0.5, -0.27], [-0.2, 0.06]];

const STYLE_ORDER = Object.keys(FIELD_PREVIEW_TOKEN_STYLES);

// Цветные зоны — под нейтральными жетонами, флаги в порядке playerColors
const orderLayers = (layers, playerColors = []) => {
  const getRank = ({legendId, color}) => {
    if (legendId === FIELD_PREVIEW_FLAG_LEGEND_ID) {
      const colorIndex = playerColors.indexOf(color);
      return colorIndex === -1 ? playerColors.length : colorIndex;
    }
    const styleRank = legendId === FIELD_PREVIEW_CENTRAL_QUARTER_LEGEND_ID
      ? STYLE_ORDER.length
      : STYLE_ORDER.indexOf(legendId);
    return playerColors.length + 1 + styleRank;
  };
  return [...layers].sort((a, b) => getRank(a) - getRank(b));
};

/**
 * Схема стартового расположения поля — все, что нужно для отрисовки без сцены.
 * Источник тот же, что у партии: файл поля (LOAD_STATE берет из него hexes и
 * units как есть), ячейки — та же геометрия, что у HexGridRenderer. Море — не
 * ячейка поля (его и сцена не считает игровой областью, см.
 * GameScene.getPlayAreaBounds), на схеме его нет.
 *
 * Ячейки и жетоны одного вида собраны в один path: на странице выбора много
 * схем, и по элементу на каждую из сотен ячеек обошлось бы дорого.
 *
 * @param {Object} fieldData — содержимое файла поля: {hexes, units, gridConfig, playerColors}
 * @returns {Object|null} модель схемы; null — в файле нет ячеек
 */
export const buildFieldPreviewModel = (fieldData) => {
  const hexes = fieldData?.hexes;
  if (!hexes) {
    return null;
  }

  const gridConfig = {...DEFAULT_GRID_CONFIG, ...fieldData.gridConfig};
  const {hexSize, orientation} = gridConfig;
  const hexMath = new HexMath(hexSize, orientation, 0, 0);
  const tokenHexMath = new HexMath(hexSize * TOKEN_HEX_SCALE, orientation, 0, 0);
  const innerHexMath = new HexMath(hexSize * INNER_HEX_SCALE, orientation, 0, 0);

  const gridPaths = [];
  // Слой — один вид жетона одного цвета: по нему легенда и порядок отрисовки
  const layersById = new Map();
  const centersByKey = {};
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  const addToLayer = (layerId, createLayer) => {
    if (!layersById.has(layerId)) {
      layersById.set(layerId, {id: layerId, count: 0, cellPaths: [], markPaths: [], polePaths: [], ...createLayer()});
    }
    const layer = layersById.get(layerId);
    layer.count += 1;
    return layer;
  };

  Object.keys(getGridGeometry({shared: {gridConfig}}).hexes).forEach((key) => {
    const token = hexes[key];
    if (token === TERRAIN_TOKENS.SEA) {
      return;
    }

    const [q, r] = key.split(',').map(Number);
    const center = hexMath.hexToPixel(q, r);
    centersByKey[key] = center;

    const cellPoints = hexMath.getHexPoints(center.x, center.y);
    gridPaths.push(formatPath(cellPoints));
    cellPoints.forEach(({x, y}) => {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    });

    if (!token) {
      return;
    }

    const tokenCellPath = formatPath(tokenHexMath.getHexPoints(center.x, center.y));

    if (checkIfFlag(token)) {
      const color = token.replace(FLAG_PREFIX, '');
      const fill = getPlayerHexColor(color);
      const layer = addToLayer(token, () => ({
        legendId: FIELD_PREVIEW_FLAG_LEGEND_ID,
        color,
        fill,
        fillOpacity: FIELD_PREVIEW_FLAG_CELL_OPACITY,
        markFill: fill,
      }));
      layer.cellPaths.push(tokenCellPath);
      layer.markPaths.push(shapeAt(center, hexSize, FLAG_CLOTH_SHAPE));
      layer.polePaths.push(shapeAt(center, hexSize, FLAG_POLE_SHAPE));
      return;
    }

    if (checkIfCentralQuarter(token)) {
      const color = token.replace(CENTRAL_QUARTER_PREFIX, '');
      const layer = addToLayer(token, () => ({
        legendId: FIELD_PREVIEW_CENTRAL_QUARTER_LEGEND_ID,
        color,
        fill: FIELD_PREVIEW_CENTRAL_QUARTER_FILL,
        markFill: getPlayerHexColor(color),
      }));
      layer.cellPaths.push(tokenCellPath);
      layer.markPaths.push(shapeAt(center, hexSize, FLAG_CLOTH_SHAPE));
      layer.polePaths.push(shapeAt(center, hexSize, FLAG_POLE_SHAPE));
      return;
    }

    const styleId = FIELD_PREVIEW_TOKEN_STYLES[token] ? token : FIELD_PREVIEW_OTHER_TOKEN;
    const {fill, mark, markFill} = FIELD_PREVIEW_TOKEN_STYLES[styleId];
    const layer = addToLayer(styleId, () => ({legendId: styleId, fill, markFill}));
    layer.cellPaths.push(tokenCellPath);
    if (mark === FIELD_PREVIEW_MARKS.INNER_HEX) {
      layer.markPaths.push(formatPath(innerHexMath.getHexPoints(center.x, center.y)));
    } else if (mark === FIELD_PREVIEW_MARKS.PEAK) {
      layer.markPaths.push(shapeAt(center, hexSize, PEAK_SHAPE));
    }
  });

  if (gridPaths.length === 0) {
    return null;
  }

  // Отряды идут поверх жетонов: нейтралы часто стоят на руинах и в городах
  const units = (fieldData.units || [])
    .filter(({hexKey}) => centersByKey[hexKey])
    .map(({id, hexKey, type, size}) => ({id, hexKey, type, size, ...centersByKey[hexKey]}));

  const padding = hexSize * VIEW_BOX_PADDING;

  const layers = [...layersById.values()].map(({cellPaths, markPaths, polePaths, ...layer}) => ({
    ...layer,
    cellPath: cellPaths.join(''),
    markPath: markPaths.join(''),
    polePath: polePaths.join(''),
  }));

  return {
    viewBox: {
      x: Math.floor(minX - padding),
      y: Math.floor(minY - padding),
      width: Math.ceil(maxX - minX + padding * 2),
      height: Math.ceil(maxY - minY + padding * 2),
    },
    hexSize,
    cellCount: gridPaths.length,
    gridPath: gridPaths.join(''),
    layers: orderLayers(layers, fieldData.playerColors),
    units,
    legendIds: [
      ...new Set([
        ...layers.map(({legendId}) => legendId),
        ...(units.length > 0 ? [FIELD_PREVIEW_UNIT_LEGEND_ID] : []),
      ]),
    ],
  };
};
